import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Import images directly
import wealthManagerBg from '../assets/wealth-manager/wealth-manager-bg.png';
import complianceOfficerBg from '../assets/wealth-manager/compliance-officer-bg.png';
import otBackground from '../assets/wealth-manager/ot-background.png';
import iaBackground from '../assets/wealth-manager/ia-background.png';
import familyOfficesBg from '../assets/wealth-manager/family-offices-bg.png';

// Create a map of images for quick access
const imageMap = {
  '/solutions/wealth-manager': wealthManagerBg,
  '/solutions/relationship-manager': wealthManagerBg,
  '/solutions/compliance-officer': complianceOfficerBg,
  '/solutions/operation-team': otBackground,
  '/solutions/cio': iaBackground,
  '/solutions/family-office': familyOfficesBg,
} as const;

// Define a type for the keys of the contentMap
type ContentPath = keyof typeof imageMap;

// Preload the wealth manager image immediately
const preloadedImage = new Image();
preloadedImage.src = wealthManagerBg;

const WMHero: React.FC = () => {
  const location = useLocation();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [currentImage, setCurrentImage] = useState<string>('');
  const [isBlurred, setIsBlurred] = useState(true);

  // Memoize the current path to prevent unnecessary re-renders
  const currentPath = useMemo(() => location.pathname as ContentPath, [location.pathname]);

  // Load image when path changes
  useEffect(() => {
    setIsImageLoaded(false);
    setIsBlurred(true);
    
    const image = imageMap[currentPath] || imageMap['/solutions/wealth-manager'];
    setCurrentImage(image);
    
    // Create a new image object to preload
    const img = new Image();
    img.src = image;
    
    img.onload = () => {
      setIsImageLoaded(true);
      // Add a small delay before removing blur for smoother transition
      setTimeout(() => setIsBlurred(false), 50);
    };
    
    img.onerror = () => {
      setIsImageLoaded(true);
      setIsBlurred(false);
    };
  }, [currentPath]);

  // Function to get content based on current path
  const getContentFromPath = () => {
    const path = location.pathname as ContentPath;
    
    const contentMap: Record<ContentPath, {
      title: string;
      heading: string;
      altText: string;
    }> = {
      '/solutions/wealth-manager': {
        title: 'Wealth Managers',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl">Power<span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Decisions</span>for<br/> Smart <span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Wealth</span></span>`,
        altText: 'Wealth Manager Background'
      },
      '/solutions/relationship-manager': {
        title: 'Relationship Managers',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl"><span class="font-bold">Enhance<br/></span><span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Client Engagement</span></span>`,
        altText: 'Relationship Manager Background'
      },
      '/solutions/compliance-officer': {
        title: 'Compliance Officers',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl"><span class="font-bold">Automated<br/></span><span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Risk Management</span></span>`,
        altText: 'Compliance Officer Background'
      },
      '/solutions/operation-team': {
        title: 'Operations Teams',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl"><span class="font-bold">Automate Optimize<br/></span><span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Deliver With Speed</span></span>`,
        altText: 'Operations Teams Background'
      },
      '/solutions/cio': {
        title: 'Investment Advisors',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl"><span class="font-bold">Sharper Insights. Smarter <br/>Decisions.</span><span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Greater Alpha.</span></span>`,
        altText: 'CIOs Background'
      },
      '/solutions/family-office': {
        title: 'Family Offices',
        heading: `<span style="line-height: 130%;" class="sm:text-5xl md:text-6xl lg:text-7xl">A <span class="bg-gradient-to-r from-[rgb(128,108,234)] to-[rgb(162,101,230)] text-transparent bg-clip-text text-purple-400 font-inter italic pr-2 text-purple-400 font-inter italic">Smarter Way </span>to Manage <br/>Generational Wealth</span>`,
        altText: 'Family Offices Background'
      }
    };
    
    return contentMap[path] || contentMap['/solutions/wealth-manager'];
  };
  
  const content = getContentFromPath();

  return (
    <div className="relative bg-primary-dark text-white">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 w-full h-full">
        {!isImageLoaded && (
          <div className="absolute inset-0 w-full h-full bg-gray-800 animate-pulse" />
        )}
        {currentImage && (
          <div className="relative w-full h-full">
            <img 
              src={currentImage}
              alt={content.altText}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
                isBlurred ? 'scale-105 blur-sm' : 'scale-100 blur-0'
              } ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              sizes="100vw"
              style={{
                willChange: 'transform, opacity, filter',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
              }}
            />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 py-24 md:py-48 flex justify-center">
        <div className="w-full flex flex-col items-center text-center">
          <div className="inline-block bg-gray-800 bg-opacity-50 text-white px-16 py-2 rounded-md mb-6 text-3xl">
            Valuefy for {content.title}
          </div>
          
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl inter"
            dangerouslySetInnerHTML={{ __html: content.heading }}
          />
          
          {/* Dot Indicator (Purple Circle) */}
          <div className="mt-8 relative">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center opacity-0">
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WMHero;
